import React, { useEffect, useState, useCallback } from 'react';
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { Box, Typography, CircularProgress, useTheme } from '@mui/material';
import { apiRequest } from '../utils/api';

/**
 * Daily P/L chart for a single position.
 * Fetches history from /api/accounts/position-pl-history and displays
 * unrealized P/L over time as an area chart with a zero reference line.
 */
export default function PositionPLChart({ accountId, symbol, underlying, livePL, liveMarketValue, liveCurrentPrice, liveUnderlyingPrice }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const profitColor = isDark ? '#388e3c' : '#4caf50';
  const lossColor = isDark ? '#d32f2f' : '#f44336';
  const axisColor = isDark ? '#9e9e9e' : '#757575';

  const [history, setHistory] = useState([]);
  const [underlyingPrices, setUnderlyingPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchHistory = useCallback(async () => {
    if (!accountId || !symbol) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await apiRequest(
        `/api/accounts/position-pl-history?accountId=${encodeURIComponent(accountId)}&symbol=${encodeURIComponent(symbol)}`
      );
      if (response.data?.success) {
        const hist = response.data.history || [];
        setHistory(hist);

        // Fetch underlying price history if we have an underlying symbol
        let underlyingSym = hist[0]?.underlying || underlying;
        if (underlyingSym === 'SPXW' || underlyingSym === 'SPX') {
          underlyingSym = '$SPX';
        }
        if (underlyingSym) {
          try {
            const earliestDate = hist[0]?.record_date;
            const days = earliestDate
              ? Math.ceil((Date.now() - new Date(earliestDate + 'T00:00:00').getTime()) / (1000 * 60 * 60 * 24)) + 10
              : 365;
            const range = days > 365 ? '2y' : days > 180 ? '1y' : days > 60 ? '6mo' : '3mo';
            const priceResp = await apiRequest(
              `/api/stocks/${encodeURIComponent(underlyingSym)}/history?range=${range}&interval=1d`
            );
            if (priceResp.data?.candles) {
              // Build a sorted array of [date, close] from candles
              const sortedCandles = priceResp.data.candles
                .map(c => ({ date: c.date, close: c.close }))
                .sort((a, b) => a.date.localeCompare(b.date));

              // Build a date->price map with forward-fill for non-trading days
              const priceMap = {};
              let lastPrice = null;
              let candleIdx = 0;

              // Iterate through all dates from earliest candle to today
              const startDate = sortedCandles[0]?.date;
              const endDate = new Date().toISOString().split('T')[0];
              if (startDate && endDate) {
                const cur = new Date(startDate + 'T00:00:00');
                const end = new Date(endDate + 'T00:00:00');
                while (cur <= end) {
                  const dateStr = cur.toISOString().split('T')[0];
                  // Advance candle pointer to find price for this date (or skip past)
                  while (candleIdx < sortedCandles.length && sortedCandles[candleIdx].date < dateStr) {
                    lastPrice = sortedCandles[candleIdx].close;
                    candleIdx++;
                  }
                  if (candleIdx < sortedCandles.length && sortedCandles[candleIdx].date === dateStr) {
                    lastPrice = sortedCandles[candleIdx].close;
                    candleIdx++;
                  }
                  if (lastPrice != null) {
                    priceMap[dateStr] = lastPrice;
                  }
                  cur.setDate(cur.getDate() + 1);
                }
              }
              setUnderlyingPrices(priceMap);
            }
          } catch (priceErr) {
            // Silently ignore — chart still works without underlying prices
          }
        }
      } else {
        setError(response.data?.error || 'Failed to load P/L history');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error fetching P/L history');
    } finally {
      setLoading(false);
    }
  }, [accountId, symbol, underlying]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">{error}</Typography>
      </Box>
    );
  }

  if (history.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">No P/L history available yet</Typography>
      </Box>
    );
  }

  const chartData = history.map(h => ({
    date: h.record_date,
    pl: parseFloat(h.unrealized_pl) || 0,
    marketValue: parseFloat(h.market_value) || 0,
    currentPrice: parseFloat(h.current_price) || 0,
    underlyingPrice: underlyingPrices[h.record_date] != null ? underlyingPrices[h.record_date] : null,
  }));

  // Overlay today's live P/L (from the position's real-time quote) so the chart's
  // latest point reflects the current session instead of the last recorded snapshot,
  // which may be from an earlier point in the day (or a prior day if the snapshot
  // job hasn't run yet for today).
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const hasLiveData = livePL != null || liveMarketValue != null || liveCurrentPrice != null;
  if (hasLiveData) {
    const lastEntry = chartData[chartData.length - 1];
    const liveEntry = {
      date: todayStr,
      pl: livePL != null ? parseFloat(livePL) || 0 : (lastEntry ? lastEntry.pl : 0),
      marketValue: liveMarketValue != null ? parseFloat(liveMarketValue) || 0 : (lastEntry ? lastEntry.marketValue : 0),
      currentPrice: liveCurrentPrice != null ? parseFloat(liveCurrentPrice) || 0 : (lastEntry ? lastEntry.currentPrice : 0),
      underlyingPrice: liveUnderlyingPrice != null ? parseFloat(liveUnderlyingPrice) || 0 : (lastEntry ? lastEntry.underlyingPrice : null),
    };
    if (lastEntry && lastEntry.date === todayStr) {
      chartData[chartData.length - 1] = liveEntry;
    } else {
      chartData.push(liveEntry);
    }
  }

  const hasUnderlying = chartData.some(d => d.underlyingPrice != null);

  const maxPL = Math.max(...chartData.map(d => d.pl), 0);
  const minPL = Math.min(...chartData.map(d => d.pl), 0);
  const padding = Math.max(Math.abs(maxPL), Math.abs(minPL)) * 0.1 || 1;

  const totalPL = chartData.length > 0 ? chartData[chartData.length - 1].pl : 0;
  const plColor = totalPL >= 0 ? profitColor : lossColor;

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const TooltipContent = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0].payload;
    return (
      <Box sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1, p: 1, boxShadow: 1 }}>
        <Typography variant="caption" fontWeight="bold">{formatDate(data.date)}</Typography>
        <Typography variant="caption" display="block" sx={{ color: data.pl >= 0 ? 'success.main' : 'error.main' }}>
          P/L: ${data.pl.toFixed(2)}
        </Typography>
        <Typography variant="caption" display="block" color="text.secondary">
          Price: ${data.currentPrice.toFixed(2)}
        </Typography>
        {data.underlyingPrice != null && (
          <Typography variant="caption" display="block" sx={{ color: '#1976d2' }}>
            Underlying: ${data.underlyingPrice.toFixed(2)}
          </Typography>
        )}
        <Typography variant="caption" display="block" color="text.secondary">
          Mkt Value: ${data.marketValue.toFixed(2)}
        </Typography>
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {chartData.length} day{chartData.length !== 1 ? 's' : ''} of history
        </Typography>
        <Typography variant="body2" fontWeight="bold" sx={{ color: plColor }}>
          Current: ${totalPL.toFixed(2)}
        </Typography>
      </Box>
      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: 10 }}>
          <defs>
            <linearGradient id="plGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={plColor} stopOpacity={0.4} />
              <stop offset="95%" stopColor={plColor} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="date"
            tickFormatter={formatDate}
            tick={{ fontSize: 11 }}
            stroke={axisColor}
          />
          <YAxis
            tick={{ fontSize: 11 }}
            stroke={axisColor}
            tickFormatter={(v) => `$${v.toFixed(0)}`}
            domain={[minPL - padding, maxPL + padding]}
          />
          {hasUnderlying && (
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 11 }}
              stroke="#1976d2"
              tickFormatter={(v) => `$${v.toFixed(0)}`}
              domain={['dataMin', 'dataMax']}
            />
          )}
          <Tooltip content={<TooltipContent />} />
          <ReferenceLine y={0} stroke={isDark ? '#616161' : '#9e9e9e'} strokeDasharray="2 2" />
          <Area
            type="monotone"
            dataKey="pl"
            stroke={plColor}
            strokeWidth={2}
            fill="url(#plGradient)"
            name="P/L"
          />
          {hasUnderlying && (
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="underlyingPrice"
              stroke="#1976d2"
              strokeWidth={1.5}
              dot={false}
              name="Underlying"
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}
